import { Link, useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertTriangle, TrendingDown, TrendingUp, UserCheck, Users } from 'lucide-react';
import clsx from 'clsx';
import { Card, EmptyState, PageHeader, Spinner } from '../../components/ui';
import { FiltersBar, useDashboardFilters } from '../../components/DashboardFilters';
import { useOverview, useSettings } from '../../api/queries';
import { formatPercent } from '../../lib/format';
import { zoneColor } from '../../lib/zone';

export function OverviewPage() {
  const { filters, effectiveThreshold } = useDashboardFilters();
  const { data, isLoading } = useOverview(filters);
  const { data: settings } = useSettings();
  const navigate = useNavigate();

  if (isLoading || !data) {
    return (
      <>
        <PageHeader title="მიმოხილვა" />
        <FiltersBar />
        <div className="flex justify-center py-16">
          <Spinner className="size-6" />
        </div>
      </>
    );
  }

  const { kpi, pipeline } = data;
  const noDataNote = kpi.noDataCount > 0 ? ` · ${kpi.noDataCount} სტუდენტს მონაცემი არ აქვს` : '';

  return (
    <>
      <PageHeader
        title="მიმოხილვა"
        subtitle={`რისკის ზღვარი ${effectiveThreshold}%${noDataNote}`}
      />

      <FiltersBar />

      {/* --- KPI --- */}
      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          icon={<Users className="size-4" />}
          label="აქტიური სტუდენტი"
          value={String(kpi.activeStudents)}
          meta={`სულ ${kpi.totalStudents}${kpi.droppedCount ? ` · ${kpi.droppedCount} შეწყვეტილი` : ''}${
            kpi.notStartedGroups.length ? ` · ${kpi.notStartedGroups.length} ჯგუფი ჯერ არ დაწყებულა` : ''
          }`}
        />
        <Kpi
          icon={<UserCheck className="size-4" />}
          label="საშუალო დასწრება"
          value={formatPercent(kpi.averagePct)}
          meta={`${kpi.measurableCount} გაზომვადი · შეწყვეტილების გარეშე`}
          tone="brand"
        />
        <Kpi
          icon={<AlertTriangle className="size-4" />}
          label="რისკში სულ"
          value={String(kpi.atRiskTotal)}
          meta={`დაუმუშავებელი ${pipeline.risk} · დამუშავებაში ${kpi.atRiskTotal - pipeline.risk}`}
          tone={kpi.atRiskTotal > 0 ? 'danger' : 'ok'}
        />
        <Kpi
          icon={<AlertTriangle className="size-4" />}
          label="დასამუშავებელი"
          value={String(kpi.untouchedCount)}
          meta="ახალი, ჯერ არ შეხებიხარ"
          tone={kpi.untouchedCount > 0 ? 'warn' : 'ok'}
        />
      </div>

      {/* --- Pipeline --- */}
      <button
        onClick={() => navigate('/attendance')}
        className="mb-5 w-full rounded-[10px] border border-line bg-surface p-4 text-left shadow-xs transition hover:border-brand-300 hover:shadow-md"
      >
        <p className="mb-3 text-[13px] font-semibold text-ink">
          გაცდენების დამუშავება
          <span className="ml-2 text-[12px] font-normal text-ink-mute">
            დააკლიკე → გაცდენების დაფა
          </span>
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          <PipelineCell icon="🔴" label="დასამუშავებელი" count={pipeline.risk} tone="danger" />
          <PipelineCell icon="📵" label="ვერ დავუკავშირდი" count={pipeline.cannotReach} tone="warn" />
          <PipelineCell icon="✉️" label="მეილი მივწერე" count={pipeline.emailSent} tone="brand" />
          <PipelineCell icon="👁" label="მონიტორინგზე" count={pipeline.monitoring} tone="neutral" />
          <PipelineCell icon="⚫" label="შეწყვიტა" count={pipeline.dropped} tone="neutral" />
        </div>
      </button>

      {/* --- ჩარტები --- */}
      <div className="mb-5 grid gap-5 lg:grid-cols-2">
        <Card title="მიმართულების მიხედვით" action={<Hint>საშუალო დასწრება</Hint>}>
          {data.byDirection.length === 0 ? (
            <EmptyState title="მონაცემი არ არის" />
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data.byDirection.map((d) => ({ name: d.name, avg: d.avg ?? 0 }))}
                  layout="vertical"
                  margin={{ left: 8, right: 16, top: 4, bottom: 4 }}
                >
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v: number) => `${v}%`}
                  />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(v: number) => [`${v}%`, 'საშუალო']}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                  <Bar dataKey="avg" radius={[0, 4, 4, 0]}>
                    {data.byDirection.map((d) => (
                      <Cell key={d.id} fill={zoneColor(d.avg, settings)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="პროექტის მიხედვით" action={<Hint>საშუალო დასწრება</Hint>}>
          {data.byProject.length === 0 ? (
            <EmptyState title="მონაცემი არ არის" />
          ) : (
            <ul className="space-y-3 py-2">
              {data.byProject.map((p) => (
                <li key={p.project.id}>
                  <div className="mb-1 flex items-center justify-between text-[13px]">
                    <span className="flex items-center gap-2 font-medium text-ink">
                      <span
                        className="size-2 shrink-0 rounded-full"
                        style={{ background: p.project.color ?? '#cfd4dc' }}
                      />
                      {p.project.name}
                    </span>
                    <span className="tabular text-ink-soft">
                      {formatPercent(p.avg)} · {p.count} სტუდენტი
                    </span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-canvas">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.min(p.avg ?? 0, 100)}%`,
                        background: zoneColor(p.avg, settings),
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* --- ჯგუფების რეიტინგი --- */}
      <div className="grid gap-5 lg:grid-cols-2">
        <Card
          title={
            <span className="flex items-center gap-2">
              <TrendingUp className="size-4 text-ok" />
              ტოპ 5 ჯგუფი
            </span>
          }
        >
          <GroupRank groups={data.topGroups} settings={settings} />
        </Card>

        <Card
          title={
            <span className="flex items-center gap-2">
              <TrendingDown className="size-4 text-danger" />
              ბოლო 5 ჯგუფი
            </span>
          }
        >
          <GroupRank groups={data.bottomGroups} settings={settings} />
        </Card>
      </div>
    </>
  );
}

// ============================================================

function Hint({ children }: { children: React.ReactNode }) {
  return <span className="text-[12px] font-normal text-ink-mute">{children}</span>;
}

const KPI_TONE = {
  neutral: 'border-line',
  brand: 'border-brand-200 bg-brand-50/40',
  ok: 'border-ok/25 bg-ok-soft/40',
  warn: 'border-warn/25 bg-warn-soft/40',
  danger: 'border-danger/25 bg-danger-soft/40',
} as const;

function Kpi({
  icon,
  label,
  value,
  meta,
  tone = 'neutral',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  meta: string;
  tone?: keyof typeof KPI_TONE;
}) {
  return (
    <div className={clsx('rounded-[10px] border bg-surface p-4 shadow-xs', KPI_TONE[tone])}>
      <div className="mb-1 flex items-center gap-1.5 text-[12px] font-medium text-ink-mute">
        {icon}
        {label}
      </div>
      <p className="tabular text-2xl font-semibold tracking-tight text-ink">{value}</p>
      <p className="mt-1 text-[11px] leading-relaxed text-ink-mute">{meta}</p>
    </div>
  );
}

const PIPELINE_TONE = {
  danger: 'text-danger',
  warn: 'text-warn',
  brand: 'text-brand-600',
  neutral: 'text-ink-mute',
} as const;

function PipelineCell({
  icon,
  label,
  count,
  tone,
}: {
  icon: string;
  label: string;
  count: number;
  tone: keyof typeof PIPELINE_TONE;
}) {
  return (
    <div className="rounded-lg bg-canvas px-2 py-2.5 text-center">
      <div className="text-base leading-none">{icon}</div>
      <div className={clsx('tabular mt-1 text-lg font-semibold', PIPELINE_TONE[tone])}>{count}</div>
      <div className="text-[10px] leading-tight text-ink-mute">{label}</div>
    </div>
  );
}

function GroupRank({
  groups,
  settings,
}: {
  groups: { id: string; name: string; course: string; avg: number; count: number }[];
  settings: { risk_threshold: number; yellow_threshold: number; red_threshold: number } | undefined;
}) {
  if (groups.length === 0) return <EmptyState title="მონაცემი არ არის" />;

  return (
    <ol className="divide-y divide-line">
      {groups.map((g, i) => (
        <li key={g.id}>
          <Link
            to={`/groups/${g.id}`}
            className="flex items-center gap-3 py-2.5 transition hover:bg-canvas/60"
          >
            <span className="tabular w-5 shrink-0 text-[12px] text-ink-mute">{i + 1}</span>
            <span className="min-w-0 flex-1">
              <span className="tabular block text-[13px] font-semibold text-ink">{g.name}</span>
              <span className="block truncate text-[11px] text-ink-mute">{g.course}</span>
            </span>
            <span
              className="tabular rounded px-2 py-0.5 text-[12px] font-medium text-white"
              style={{ background: zoneColor(g.avg, settings) }}
            >
              {g.avg.toFixed(1)}%
            </span>
          </Link>
        </li>
      ))}
    </ol>
  );
}
